"use client";

import { useState, useEffect, useContext, useRef, useMemo } from "react";
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Button,
  createTheme,
  ThemeProvider,
  Autocomplete,
  Typography,
  Chip,
  alpha,
  Tooltip,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Popper,
  useTheme,
  Collapse,
  useMediaQuery,
  Drawer,
  Divider,
  Badge,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import ReplayIcon from "@mui/icons-material/Replay";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PersonIcon from "@mui/icons-material/Person";
import InventoryIcon from "@mui/icons-material/Inventory";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PercentIcon from "@mui/icons-material/Percent";
import BarcodeIcon from "@mui/icons-material/QrCode";
import BarChartIcon from "@mui/icons-material/BarChart";
import ChannelIcon from "@mui/icons-material/Tv";
import TuneIcon from "@mui/icons-material/Tune";
import CloseIcon from "@mui/icons-material/Close";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import BadgeIcon from "@mui/icons-material/Badge";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import CustomPagination from "./CustomPagination"; // Componente de paginación personalizado.
import { ErpContext } from "../../context/ErpContext";
import GraficasDetallesVentasModal from "./graficas/graficas-detalles-ventas-modal";

// Enhanced theme with more modern styling
const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#5b5b5f",
    },
    secondary: {
      main: "rgb(244, 177, 14)",
    },
    background: {
      default: "#f8f9fa",
      paper: "#ffffff",
    },
    text: {
      primary: "#2d3748",
      secondary: "#718096",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    button: {
      textTransform: "none",
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 10,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: "none",
          boxShadow: "none",
          fontWeight: 500,
          "&:hover": {
            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
          },
        },
        containedPrimary: {
          "&:hover": {
            backgroundColor: "#4a4a4e",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          borderRadius: 6,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 8,
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          borderRadius: 8,
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          "&:hover": {
            backgroundColor: alpha("#5b5b5f", 0.08),
          },
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: "none",
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#5b5b5f",
            color: "#fff",
            borderBottom: "none",
            borderRadius: "8px 8px 0 0",
            padding: "0 16px",
            height: "56px",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            color: "white !important",
            fontWeight: 600,
            fontSize: "0.875rem",
          },
          "& .MuiDataGrid-columnHeaderTitleContainer": {
            padding: "0 8px",
          },
          "& .MuiDataGrid-menuIconButton": {
            color: "rgba(255, 255, 255, 0.7) !important",
            "&:hover": {
              color: "white !important",
              backgroundColor: "rgba(255, 255, 255, 0.1)",
            },
          },
          "& .MuiDataGrid-iconButtonContainer": {
            color: "white !important",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "1px solid rgba(224, 224, 224, 0.5)",
            padding: "0 16px",
            fontSize: "0.875rem",
          },
          "& .MuiDataGrid-row": {
            "&:nth-of-type(even)": {
              backgroundColor: "rgba(0, 0, 0, 0.02)",
            },
            "&:hover": {
              backgroundColor: "rgba(66, 153, 225, 0.08)",
              transition: "background 0.2s ease",
            },
            transition: "background 0.2s ease",
            cursor: "pointer",
          },
          "& .MuiDataGrid-footerContainer": {
            backgroundColor: "#fff",
            borderTop: "1px solid rgba(224, 224, 224, 0.5)",
            display: "none !important",
          },
          "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
            width: "8px",
            height: "8px",
          },
          "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track": {
            background: "#f1f1f1",
            borderRadius: "10px",
          },
          "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
            backgroundColor: "#c1c1c1",
            borderRadius: "10px",
            "&:hover": {
              backgroundColor: "#a1a1a1",
            },
          },
        },
      },
    },
  },
});

const TablaDetallesVentas = () => {
  const theme = useTheme()
  const {
    getTransaccionItemByTipoTransaccion,
    getTransaccionItemByTipoTransaccionAllData,
    getEntidadesClientesTresLetras,
    getItemTresLetras,
    itemTresLetras,
    entidadesClientesTresLetras,
    arrayItemTresLetras,
    getUbicacion,
    ubicaciones,
    isNavbarCollapsed,
    getParametrosGlobales,
    getCanalEntidad,
    entidadAtributoNoEliminados,
    getEntidadAtributoNoEliminados,
    getEntidadAtributoClasificacionByIdAtributo,
    getFechaHoraActualClient,
  } = useContext(ErpContext);
  const [rows, setRows] = useState([]);
  const [filterCode, setFilterCode] = useState("");
  const [startDate, setStartDate] = useState(dayjs().subtract(30, "day"));
  const [endDate, setEndDate] = useState(dayjs());
  const [page, setPage] = useState(1);
  const [limit] = useState(100);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const isFirstRender = useRef(true);
  const [searchText, setSearchText] = useState("");
  const [searchTextCliente, setSearchTextCliente] = useState("");
  const [dniCliente, setDniCliente] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showItemDropdown, setShowItemDropdown] = useState(false)
  const itemSearchRef = useRef(null)
  const [filteredItems, setFilteredItems] = useState([]);
  const [selectedUbicacion, setSelectedUbicacion] = useState(null);
  const [filtersActive, setFiltersActive] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);
  const [idTransaccionSearch, setIdTransaccionSearch] = useState(null);
  
  // Estados para canal de entidad
  const [canalesEntidades, setCanalesEntidades] = useState([])
  const [selectedCanalEntidad, setSelectedCanalEntidad] = useState(null)
  
  // Estados para filtros adicionales
  const [masFiltrosActive, setMasFiltrosActive] = useState(false)

  // ===== Responsive flags =====
  const isLaptopDown = useMediaQuery("(max-width:1200px)")
  const isMobile = useMediaQuery("(max-width:600px)")

  // Estados para el menú lateral (solo desktop)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [mostrarFiltroCliente, setMostrarFiltroCliente] = useState(false)
  const [mostrarFiltroProducto, setMostrarFiltroProducto] = useState(false)
  const [mostrarFiltroCanal, setMostrarFiltroCanal] = useState(false)

  // Estados para filtros de atributos de entidad
  const [opcionesPorAtributo, setOpcionesPorAtributo] = useState({})
  const [atributosEntidadFiltro, setAtributosEntidadFiltro] = useState({})

  // Estados para gráficas
  const [openGraficasModal, setOpenGraficasModal] = useState(false);
  const [loadingGraficas, setLoadingGraficas] = useState(false);
  const [datosGraficas, setDatosGraficas] = useState([]);

  // Columnas generadas dinámicamente usando useMemo
  const columns = useMemo(() => {
    const baseColumns = [
    {
      field: "ventaId",
      headerName: "Nro Venta",
      headerAlign: "center",
      flex: 0.7,
      minWidth: 70,
      renderCell: (params) => (
        <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 1 }}>
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: "fecha",
      headerName: "Fecha",
      headerAlign: "center",
      flex: 0.8,
      width: 150,
      sortable: true,
      valueGetter: (value, row) => row.fechaHoraCreacion || row.fecha,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CalendarTodayIcon
            fontSize="small"
            sx={{ color: alpha("#5b5b5f", 0.7) }}
          />
          <Typography variant="body2">{params.row.fecha}</Typography>
        </Box>
      ),
    },
    {
      field: "montoTotal",
      headerName: "Monto",
      headerAlign: "center",
      type: "number",
      flex: 0.8,
      width: 150,
      renderCell: (params) => (
        <Chip
          icon={<AttachMoneyIcon fontSize="small" />}
          label={params.value?.toLocaleString("es-AR") || "0.00"}
          size="small"
          sx={{
            fontWeight: "bold",
            bgcolor: alpha("#4CAF50", 0.1),
            color: "#4CAF50",
            "& .MuiChip-icon": { color: "#4CAF50" },
          }}
        />
      ),
    },
    {
      field: "codigo",
      headerName: "Código",
      headerAlign: "center",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          variant="outlined"
          sx={{
            fontWeight: 500,
            borderColor: "rgb(244, 147, 14)",
            color: "rgb(244, 147, 14)",
          }}
        />
      ),
    },
    {
      field: "codigoScanner",
      headerName: "Código de barras",
      headerAlign: "center",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <BarcodeIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.7) }} />
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: "descripcion",
      headerName: "Descripción",
      headerAlign: "center",
      flex: 1.2,
      minWidth: 220,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <InventoryIcon
            fontSize="small"
            sx={{ color: alpha("#5b5b5f", 0.7) }}
          />
          <Tooltip title={params.value}>
            <Typography variant="body2" noWrap fontWeight={"500"}>
              {params.value}
            </Typography>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: "ubicacion",
      headerName: "Ubicación",
      headerAlign: "center",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <LocationOnIcon
            fontSize="small"
            sx={{ color: alpha("#5b5b5f", 0.7) }}
          />
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: "cliente",
      headerName: "Cliente",
      headerAlign: "center",
      flex: 1.2,
      minWidth: 150,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PersonIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.7) }} />
          <Tooltip title={params.value}>
            <Typography variant="body2" noWrap>
              {params.value}
            </Typography>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: "cantidad",
      headerName: "Cantidad",
      headerAlign: "center",
      type: "number",
      width: 100,
      flex: 0.5,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          sx={{
            fontWeight: "bold",
            bgcolor: alpha("#3182ce", 0.1),
            color: "#3182ce",
          }}
        />
      ),
    },
    {
      field: "precio",
      headerName: "Precio",
      headerAlign: "center",
      type: "number",
      width: 120,
      flex: 0.8,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: "500", color: "#4CAF50" }}
          >
            ${params.value?.toLocaleString("es-AR") || "0.00"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "porcentajeDescuento",
      headerName: "Desc (%)",
      headerAlign: "center",
      type: "number",
      width: 110,
      flex: 0.7,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: params.value > 0 ? "bold" : "normal",
              color: params.value > 0 ? "#e53e3e" : "inherit",
            }}
          >
            {params.value}%
          </Typography>
        </Box>
      ),
    },
  ];

  // Agregar columna de canal si está habilitado
  if (canalesEntidades && canalesEntidades.length > 0) {
    baseColumns.splice(-1, 0, {
      field: "canal",
      headerName: "Canal",
      headerAlign: "center",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ChannelIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.7) }} />
          <Typography variant="body2">{params.value || "Sin canal"}</Typography>
        </Box>
      ),
    });
  }

  return baseColumns;
}, [canalesEntidades]);

  // Función para realizar la consulta usando paginado y filtros.
  const fetchData = async (pageToLoad = page) => {
    try {
      setLoading(true);
      
      // Convertir atributosEntidadFiltro a atributoEntidad1, atributoEntidad2, etc.
      const atributosParams = {}
      Object.keys(atributosEntidadFiltro).forEach((key) => {
        const valor = atributosEntidadFiltro[key]
        if (valor) {
          atributosParams[`atributoEntidad${key}`] = valor
        }
      })

      // ✅ Normalizar fechas con timezone según políticas del sistema
      let startDateNormalized = null;
      let endDateNormalized = null;
      
      if (startDate) {
        const formattedStartDate = startDate.format("YYYY-MM-DD");
        const resultStart = getFechaHoraActualClient(formattedStartDate);
        startDateNormalized = dayjs(resultStart.fechaServidor).startOf("day").toDate();
      }
      
      if (endDate) {
        const formattedEndDate = endDate.format("YYYY-MM-DD");
        const resultEnd = getFechaHoraActualClient(formattedEndDate);
        endDateNormalized = dayjs(resultEnd.fechaServidor).endOf("day").toDate();
      }

      const params = {
        idTipoTransaccion: 1,
        page: pageToLoad,
        limit,
        startDate: startDateNormalized,
        endDate: endDateNormalized,
        idEntidad: dniCliente?.id || null,
        idItem: selectedItem?.id || null,
        idUbicacion: selectedUbicacion?.id || null,
        idTransaccionSearch: idTransaccionSearch || null,
        canalEntidad: selectedCanalEntidad?.id || null,
        ...atributosParams,
      };

      // Se asume que getTransaccionItemByTipoTransaccion devuelve un objeto con:
      // { total, totalPages, currentPage, data: [...] }
      const response = await getTransaccionItemByTipoTransaccion(params);

      if (response) {
        const { data, total, totalPages: tPages, currentPage } = response;
        // Se construyen las filas para el DataGrid.
        const flattenedRows = data.map((tItem) => {
          const fechaRaw = tItem.Transaccion?.fechaHoraCreacion || tItem.Transaccion?.fecha;
          const fechaFormateada = fechaRaw
            ? new Intl.DateTimeFormat("es-AR", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              }).format(new Date(fechaRaw))
            : "";
          return {
          id: `${tItem.Transaccion.id}-${tItem.idItem}`,
          // Nro Venta: concatenación de idItem e idTransaccion.
          ventaId: `${tItem.idItem}-${tItem.Transaccion.id}`,
          // Fecha formateada (como tablaVentas) + timestamp para ordenamiento correcto
          fecha: fechaFormateada,
          fechaHoraCreacion: fechaRaw,
          codigo: tItem.item?.codigo || "",
          codigoScanner: tItem.item?.codigoScanner || "",
          descripcion: tItem.item?.descripcion || "",
          cantidad: tItem.cantidad,
          precio: tItem.precio,
          montoTotal: tItem.montoTotal,
          porcentajeDescuento: tItem.porcentajeDescuento,
          cliente:
            tItem.Transaccion.entidad.descripcion ||
            "" + " " + tItem.Transaccion.entidad.apellido ||
            "",
          ubicacion: tItem.Transaccion.ubicacion.descripcion,
          canal: tItem.Transaccion.entidad?.canalEntidad?.descripcion || "",
        };
        });

        // Calcular el monto total de todos los items
        const totalSum = flattenedRows.reduce(
          (sum, row) => sum + (row.montoTotal || 0),
          0
        );
        setTotalAmount(totalSum);

        setRows(flattenedRows);
        setTotalCount(total);
        setTotalPages(tPages);
        setPage(currentPage);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Cargar opciones para cada atributo cuando cambian los atributos disponibles
  const cargarOpcionesAtributos = async () => {
    if (!entidadAtributoNoEliminados || entidadAtributoNoEliminados.length === 0) {
      setOpcionesPorAtributo({})
      return
    }

    const opcionesMap = {}
    for (const attr of entidadAtributoNoEliminados) {
      try {
        const atributoId = attr.idEntidadAtributo || attr.id
        const opciones = await getEntidadAtributoClasificacionByIdAtributo(atributoId, 1) // 1 = Cliente
        opcionesMap[atributoId] = opciones || []
      } catch (error) {
        console.error(`Error al cargar opciones para atributo ${attr.idEntidadAtributo || attr.id}:`, error)
        opcionesMap[attr.idEntidadAtributo || attr.id] = []
      }
    }
    setOpcionesPorAtributo(opcionesMap)
  }

  // Inicializar opciones cuando se cargan los atributos disponibles
  useEffect(() => {
    if (entidadAtributoNoEliminados && entidadAtributoNoEliminados.length > 0) {
      cargarOpcionesAtributos()
    } else if (entidadAtributoNoEliminados && entidadAtributoNoEliminados.length === 0) {
      setOpcionesPorAtributo({})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entidadAtributoNoEliminados])

  // Al montar el componente se realiza la consulta inicial.
  useEffect(() => {
    const fetchDataInicio = async () => {
      try {
        await getUbicacion();
        
        // Cargar atributos de entidad Cliente (ID 1)
        await getEntidadAtributoNoEliminados(1)
        
        // Cargar canales de entidad si está habilitado
        const canalEntidad = await getParametrosGlobales(["canalEntidad"])
        // console.log("canalEntidad canalEntidad :", canalEntidad.canalEntidad)
        if (canalEntidad.canalEntidad == 1) {
          const datosCanalesEntidades = await getCanalEntidad()
          // console.log("datosCanalesEntidades:", datosCanalesEntidades)
          setCanalesEntidades(datosCanalesEntidades)
        }
        
        fetchData(1);
      } catch (error) {
        console.error("Error al cargar datos iniciales:", error)
      }
    };
    
    fetchDataInicio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (searchTextCliente.length >= 3) {
      getEntidadesClientesTresLetras(searchTextCliente, 1); // Llama a la API
    } else {
      setFilteredItems([]); // Vacía las opciones si hay menos de 3 letras
    }
  }, [searchTextCliente]); //Este paramatro searchText condiciona al useEffect, detectando sus cambios. Solo se ejecuta si cambia el valor del input de searchText.

  // Efecto para búsqueda de ítems
  useEffect(() => {
    if (searchText.length >= 3) {
      arrayItemTresLetras([]);
      getItemTresLetras(searchText);
      setShowItemDropdown(true)
    } else {
      arrayItemTresLetras([]);
      setFilteredItems([]);
      setShowItemDropdown(false)
    }
  }, [searchText]);

  // Cuando cambie la página (por la UI de paginado), se vuelve a consultar.
  useEffect(() => {
    if (!isFirstRender.current) {
      fetchData(page);
    } else {
      isFirstRender.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // ===== Control del menú lateral =====
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen)
  }

  // Función para alternar filtros y cerrar otros
  const toggleFiltro = (filtroNombre) => {
    switch (filtroNombre) {
      case 'cliente':
        setMostrarFiltroCliente(!mostrarFiltroCliente)
        setMostrarFiltroProducto(false)
        setMostrarFiltroCanal(false)
        break
      case 'producto':
        setMostrarFiltroProducto(!mostrarFiltroProducto)
        setMostrarFiltroCliente(false)
        setMostrarFiltroCanal(false)
        break
      case 'canal':
        setMostrarFiltroCanal(!mostrarFiltroCanal)
        setMostrarFiltroCliente(false)
        setMostrarFiltroProducto(false)
        break
      default:
        break
    }
  }

  // Verificar si hay filtros activos
  useEffect(() => {
    const hasActiveFilters =
      (endDate && startDate && !endDate.isSame(startDate, "day")) ||
      dniCliente ||
      selectedItem ||
      selectedUbicacion ||
      selectedCanalEntidad ||
      Object.values(atributosEntidadFiltro).some(v => v);

    setFiltersActive(hasActiveFilters);
  }, [startDate, endDate, dniCliente, selectedItem, selectedUbicacion, selectedCanalEntidad, atributosEntidadFiltro]);

  // Al presionar el botón "Buscar" se resetea a la página 1 y se ejecuta el fetch.
  const handleBuscar = () => {
    setPage(1);
    fetchData(1);
  };

  // Función para abrir el modal de gráficas
  const handleOpenGraficasModal = async () => {
    setLoadingGraficas(true);
    setOpenGraficasModal(true);

    try {
      // Convertir atributosEntidadFiltro a atributoEntidad1, atributoEntidad2, etc.
      const atributosParams = {}
      Object.keys(atributosEntidadFiltro).forEach((key) => {
        const valor = atributosEntidadFiltro[key]
        if (valor) {
          atributosParams[`atributoEntidad${key}`] = valor
        }
      })

      // ✅ Normalizar fechas con timezone según políticas del sistema
      let startDateNormalized = null;
      let endDateNormalized = null;
      
      if (startDate) {
        const formattedStartDate = startDate.format("YYYY-MM-DD");
        const resultStart = getFechaHoraActualClient(formattedStartDate);
        startDateNormalized = dayjs(resultStart.fechaServidor).startOf("day").toDate();
      }
      
      if (endDate) {
        const formattedEndDate = endDate.format("YYYY-MM-DD");
        const resultEnd = getFechaHoraActualClient(formattedEndDate);
        endDateNormalized = dayjs(resultEnd.fechaServidor).endOf("day").toDate();
      }

      const params = {
        idTipoTransaccion: 1, // Ventas
        startDate: startDateNormalized,
        endDate: endDateNormalized,
        idEntidad: dniCliente?.id || null,
        idItem: selectedItem?.id || null,
        idUbicacion: selectedUbicacion?.id || null,
        idTransaccionSearch: idTransaccionSearch || null,
        canalEntidad: selectedCanalEntidad?.id || null,
        ...atributosParams,
      };

      // Llamar a la nueva función SIN PAGINADO para obtener todos los datos
      const allData = await getTransaccionItemByTipoTransaccionAllData(params);
      console.log("allData ", allData)

      setDatosGraficas(allData.data);
    } catch (error) {
      console.error("Error al cargar datos para gráficas:", error);
      setDatosGraficas([]);
    } finally {
      setLoadingGraficas(false);
    }
  };

  // Función para cerrar el modal de gráficas
  const handleCloseGraficasModal = () => {
    setOpenGraficasModal(false);
    setDatosGraficas([]);
  };

  // Resetear filtros
  const resetFilters = () => {
    setDniCliente(null);
    setSelectedItem(null);
    setSelectedUbicacion(null);
    setSearchText("");
    setSearchTextCliente("");
    setIdTransaccionSearch("");
    setSelectedCanalEntidad(null);
    setAtributosEntidadFiltro({});
    setMostrarFiltroCliente(false)
    setMostrarFiltroProducto(false)
    setMostrarFiltroCanal(false)
    setMasFiltrosActive(false)

    // Mantener solo la fecha de inicio como hoy - 30 días
    const today = dayjs();
    setStartDate(today.subtract(30, "day"));
    setEndDate(today);

    // Refrescar datos con filtros reseteados
    setPage(1);
    fetchData(1);
  };

  // Función para renderizar el contenido de los filtros (reutilizable)
  const renderFiltrosContent = () => (
    <>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          alignItems: "center",
          justifyContent: "space-between",
          flexDirection: isLaptopDown ? "column" : "row",
        }}
      >
        {/* Filtro de fechas - PRIMERA PRIORIDAD */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, flex: 1, minWidth: isLaptopDown ? "100%" : 250, width: isLaptopDown ? "100%" : "auto" }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
              format="DD/MM/YYYY"
              label="Desde"
              slotProps={{ textField: { size: "small", inputProps: { maxLength: 10 }, fullWidth: isLaptopDown } }}
              sx={{ flex: 1 }}
            />
            <DatePicker
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
              format="DD/MM/YYYY"
              label="Hasta"
              slotProps={{ textField: { size: "small", inputProps: { maxLength: 10 }, fullWidth: isLaptopDown } }}
              sx={{ flex: 1 }}
            />
          </LocalizationProvider>
        </Box>

        {/* Filtro de ID, ubicación y cliente - SEGUNDA PRIORIDAD */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 2, minWidth: isLaptopDown ? "100%" : 300, width: isLaptopDown ? "100%" : "auto", flexDirection: isLaptopDown ? "column" : "row" }}>
          <TextField
            label="Buscar por ID"
            type="number"
            size="small"
            value={idTransaccionSearch}
            onChange={(e) => setIdTransaccionSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ width: isLaptopDown ? "100%" : "30%" }}
            fullWidth={isLaptopDown}
          />
          <Autocomplete
            size="small"
            disablePortal
            options={ubicaciones.map((it) => ({ id: it.id, label: it.descripcion }))}
            value={selectedUbicacion}
            onChange={(event, newValue) => setSelectedUbicacion(newValue)}
            sx={{ flex: 1, width: isLaptopDown ? "100%" : "auto" }}
            renderInput={(params) => <TextField {...params} label="Ubicación" variant="outlined" />}
          />
          <Autocomplete
            size="small"
            disablePortal
            options={entidadesClientesTresLetras.map((it) => ({
              label: `${it.descripcion} ${it.apellido} ${it.dniCuitCuil}`,
              descripcion: it.descripcion || null,
              dniCuitCuil: it.dniCuitCuil,
              id: it.id,
              apellido: it.apellido,
              telefono: it.telefono || null,
              email: it.email || null,
              direccion: it.direccion || null,
              localidad: it.localidad || null,
              provincia: it.provincia || null,
              idTipoEntidad: it.idTipoEntidad,
            }))}
            value={dniCliente}
            onChange={(event, newValue) => setDniCliente(newValue)}
            onInputChange={(event, newInputValue) => setSearchTextCliente(newInputValue)}
            sx={{ flex: 1, width: isLaptopDown ? "100%" : "auto" }}
            renderInput={(params) => <TextField {...params} label="Cliente - DNI / CUIT / CUIL" variant="outlined" />}
          />
        </Box>

        {/* Botones de acción (solo desktop) */}
        {!isLaptopDown && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              onClick={handleBuscar}
              disabled={loading}
              startIcon={<SearchIcon />}
              sx={{
                backgroundColor: "#5b5b5f",
                textTransform: "none",
                padding: "8px 16px",
                color: "white",
                "&:hover": { backgroundColor: alpha("rgb(244, 177, 14)", 0.8) },
              }}
            >
              Buscar
            </Button>

            <Button
              variant="outlined"
              size="small"
              onClick={handleOpenGraficasModal}
              disabled={loading}
              startIcon={<BarChartIcon />}
              sx={{
                borderColor: "rgb(244, 177, 14)",
                color: "rgb(244, 177, 14)",
                textTransform: "none",
                padding: "8px 16px",
                "&:hover": {
                  borderColor: alpha("rgb(244, 177, 14)", 0.8),
                  backgroundColor: alpha("rgb(244, 177, 14)", 0.1),
                },
              }}
            >
              Gráficas
            </Button>

            <Tooltip title={filtersActive ? "Resetear filtros" : "Refrescar"}>
              <IconButton
                onClick={resetFilters}
                color="primary"
                size="small"
                sx={{
                  bgcolor: filtersActive ? alpha(theme.palette.secondary.main, 0.1) : "transparent",
                  "&:hover": { bgcolor: alpha(theme.palette.secondary.main, 0.2) },
                }}
              >
                <ReplayIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title={sidebarOpen ? "Cerrar Más Filtros" : "Mostrar Más Filtros"}>
              <Badge 
                badgeContent={
                  (selectedItem ? 1 : 0) +
                  (selectedCanalEntidad ? 1 : 0) +
                  (Object.values(atributosEntidadFiltro).filter(v => v).length > 0 ? 1 : 0)
                } 
                color="secondary"
              >
                <IconButton
                  onClick={toggleSidebar}
                  color="primary"
                  size="small"
                  sx={{
                    bgcolor: sidebarOpen ? alpha(theme.palette.secondary.main, 0.1) : "transparent",
                    "&:hover": { bgcolor: alpha(theme.palette.secondary.main, 0.2) },
                  }}
                >
                  <TuneIcon />
                </IconButton>
              </Badge>
            </Tooltip>
          </Box>
        )}
      </Box>

        {/* Boxes de filtros activos (debajo del menú principal) */}
        {!isLaptopDown && (dniCliente || selectedItem || selectedUbicacion || selectedCanalEntidad || Object.values(atributosEntidadFiltro).some(v => v)) && (
          <Box
            sx={{
            px: 0,
            pt: 2,
            pb: 0,
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            alignItems: "center",
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            mt: 2,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mr: 1, color: theme.palette.text.secondary }}>
            Filtros activos:
          </Typography>
          
          {/* Chip de Cliente */}
          {dniCliente && (
            <Chip
              label={`Cliente: ${dniCliente.descripcion || dniCliente.label || dniCliente.dniCuitCuil || ""}`}
              size="small"
              onDelete={() => {
                setDniCliente(null)
                setSearchTextCliente("")
                setMostrarFiltroCliente(false)
                handleBuscar()
              }}
              sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                fontWeight: 500,
              }}
            />
          )}

          {/* Chip de Producto */}
          {selectedItem && (
            <Chip
              label={`Producto: ${selectedItem.descripcion || selectedItem.label || ""}`}
              size="small"
              onDelete={() => {
                setSelectedItem(null)
                setSearchText("")
                setMostrarFiltroProducto(false)
                handleBuscar()
              }}
              sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                fontWeight: 500,
              }}
            />
          )}

          {/* Chip de Ubicación */}
          {selectedUbicacion && (
            <Chip
              label={`Ubicación: ${selectedUbicacion.label || selectedUbicacion.descripcion || ""}`}
              size="small"
              onDelete={() => {
                setSelectedUbicacion(null)
                handleBuscar()
              }}
              sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                fontWeight: 500,
              }}
            />
          )}

          {/* Chip de Canal */}
          {selectedCanalEntidad && (
            <Chip
              label={`Canal: ${selectedCanalEntidad.label || selectedCanalEntidad.descripcion || ""}`}
              size="small"
              onDelete={() => {
                setSelectedCanalEntidad(null)
                setMostrarFiltroCanal(false)
                handleBuscar()
              }}
              sx={{
                backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                color: theme.palette.secondary.main,
                fontWeight: 500,
              }}
            />
          )}

          {/* Chips de Atributos de Entidad */}
          {entidadAtributoNoEliminados && entidadAtributoNoEliminados.length > 0 && entidadAtributoNoEliminados.map((atributo, index) => {
            const atributoNumero = index + 1
            const valorAtributo = atributosEntidadFiltro[atributoNumero]
            if (!valorAtributo) return null
            
            const atributoId = atributo.idEntidadAtributo || atributo.id
            const opciones = opcionesPorAtributo[atributoId] || []
            const opcionSeleccionada = opciones.find(o => o.id === valorAtributo)
            
            return (
              <Chip
                key={atributoId}
                label={`${atributo.descripcion}: ${opcionSeleccionada?.descripcion || "N/A"}`}
                size="small"
                onDelete={() => {
                  setAtributosEntidadFiltro(prev => {
                    const newAtributos = { ...prev }
                    delete newAtributos[atributoNumero]
                    return newAtributos
                  })
                  handleBuscar()
                }}
                sx={{
                  backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                  color: theme.palette.secondary.main,
                  fontWeight: 500,
                }}
              />
            )
          })}
        </Box>
      )}

      {/* Boxes de filtros expandibles (debajo del menú principal) */}
      {!isLaptopDown && (mostrarFiltroCliente || mostrarFiltroProducto || mostrarFiltroCanal) && (
        <Box
          sx={{
            px: 0,
            pt: 2,
            pb: 0,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            mt: 2,
          }}
        >
          {/* Subbox de Atributos de Entidad */}
          {mostrarFiltroCliente && (
            <Paper
              elevation={2}
              sx={{
                p: 2,
                backgroundColor: alpha(theme.palette.background.default, 0.5),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="subtitle2" sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 600, color: "#5b5b5f" }}>
                  <PersonIcon fontSize="small" sx={{ color: "rgb(244, 177, 14)" }} />
                  Filtrar por Atributos de Entidad
                </Typography>
                <IconButton size="small" onClick={() => setMostrarFiltroCliente(false)} sx={{ color: "#5b5b5f" }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              {/* Filtros por Atributos de Entidad */}
              <Box sx={{ mb: 2 }}>
                {entidadAtributoNoEliminados && Array.isArray(entidadAtributoNoEliminados) && entidadAtributoNoEliminados.length > 0 && (
                  <Chip 
                    label={`${entidadAtributoNoEliminados.length} atributos disponibles`} 
                    size="small" 
                    sx={{ mb: 2, color: "#5b5b5f" }} 
                  />
                )}
                
                {(() => {
                  const tieneAtributos = entidadAtributoNoEliminados && Array.isArray(entidadAtributoNoEliminados) && entidadAtributoNoEliminados.length > 0
                  
                  if (!tieneAtributos) {
                    return (
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontStyle: "italic" }}>
                        No hay atributos de entidad configurados. Ve a Administración → Atributos de Entidad para configurarlos.
                      </Typography>
                    )
                  }
                  
                  return (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(3, 1fr)", lg: "repeat(4, 1fr)" },
                        gap: 2,
                      }}
                    >
                      {entidadAtributoNoEliminados.map((atributo, index) => {
                        const atributoNumero = index + 1
                        const atributoId = atributo.idEntidadAtributo || atributo.id
                        const opciones = opcionesPorAtributo[atributoId] || []
                        
                        return (
                          <Autocomplete
                            key={atributoId}
                            size="small"
                            disablePortal
                            options={opciones}
                            getOptionLabel={(option) => option.descripcion || ""}
                            value={opciones.find(o => o.id === atributosEntidadFiltro[atributoNumero]) || null}
                            onChange={(event, newValue) => {
                              setAtributosEntidadFiltro(prev => {
                                const newAtributos = { ...prev }
                                if (newValue) {
                                  newAtributos[atributoNumero] = newValue.id
                                } else {
                                  delete newAtributos[atributoNumero]
                                }
                                return newAtributos
                              })
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={atributo.descripcion}
                                variant="outlined"
                                size="small"
                                InputProps={{
                                  ...params.InputProps,
                                  startAdornment: (
                                    <>
                                      <InputAdornment position="start">
                                        <PersonIcon fontSize="small" sx={{ color: "rgb(244, 177, 14)" }} />
                                      </InputAdornment>
                                      {params.InputProps.startAdornment}
                                    </>
                                  ),
                                }}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    backgroundColor: atributosEntidadFiltro[atributoNumero]
                                      ? alpha(theme.palette.secondary.main, 0.05)
                                      : "white",
                                  },
                                }}
                              />
                            )}
                          />
                        )
                      })}
                    </Box>
                  )
                })()}
              </Box>

              {/* Botones de Acción */}
              <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 2 }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleBuscar}
                  startIcon={<SearchIcon sx={{ color: "white" }} />}
                  sx={{
                    backgroundColor: "#5b5b5f",
                    textTransform: "none",
                    color: "white",
                    "&:hover": { backgroundColor: alpha("rgb(244, 177, 14)", 0.8) },
                    "& .MuiButton-startIcon svg": {
                      color: "white",
                    },
                  }}
                >
                  Aplicar Filtros
                </Button>
                {Object.values(atributosEntidadFiltro).filter(v => v).length > 0 && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setAtributosEntidadFiltro({})
                      handleBuscar()
                    }}
                    sx={{ 
                      textTransform: "none",
                      color: "#5b5b5f",
                      borderColor: "#5b5b5f",
                      "&:hover": {
                        borderColor: "rgb(244, 177, 14)",
                        backgroundColor: alpha("rgb(244, 177, 14)", 0.1),
                      },
                    }}
                  >
                    Limpiar Atributos
                  </Button>
                )}
              </Box>
            </Paper>
          )}

          {/* Subbox de Producto */}
          {mostrarFiltroProducto && (
            <Paper
              elevation={2}
              sx={{
                p: 2,
                backgroundColor: alpha(theme.palette.background.default, 0.5),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="subtitle2" sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 600, color: "#5b5b5f" }}>
                  <InventoryIcon fontSize="small" sx={{ color: "rgb(244, 177, 14)" }} />
                  Buscar Producto
                </Typography>
                <IconButton size="small" onClick={() => setMostrarFiltroProducto(false)} sx={{ color: "#5b5b5f" }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              
              <Box sx={{ position: "relative", display: "flex", gap: 2, alignItems: "center" }}>
                <TextField
                  size="small"
                  fullWidth
                  label="Buscar producto"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onFocus={() => {
                    if (itemTresLetras && itemTresLetras.length > 0) {
                      setShowItemDropdown(true)
                    }
                  }}
                  onBlur={(e) => {
                    setTimeout(() => {
                      setShowItemDropdown(false)
                    }, 200)
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  inputRef={itemSearchRef}
                  sx={{ flex: 1 }}
                />
                <Popper
                  open={showItemDropdown && (itemTresLetras || []).length > 0}
                  anchorEl={itemSearchRef.current}
                  placement="bottom-start"
                  style={{ width: itemSearchRef.current?.offsetWidth, zIndex: 1300 }}
                  modifiers={[
                    {
                      name: "offset",
                      options: {
                        offset: [0, 4],
                      },
                    },
                  ]}
                >
                  <Paper
                    elevation={3}
                    sx={{
                      maxHeight: 300,
                      overflow: "auto",
                      width: "100%",
                      mt: 0.5,
                    }}
                  >
                    <List dense>
                      {(itemTresLetras || []).map((item, index) => (
                        <ListItem
                          key={item.id || index}
                          button
                          onClick={() => {
                            const itemObj = {
                              label: `${item.descripcion} ${item.codigo} ${item.codigoScanner}`,
                              descripcion: item.descripcion,
                              codigo: item.codigo,
                              id: item.id,
                              codigoScanner: item.codigoScanner,
                            }
                            setSelectedItem(itemObj)
                            setShowItemDropdown(false)
                            setSearchText("")
                          }}
                          sx={{
                            "&:hover": {
                              backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            },
                          }}
                        >
                          <ListItemText
                            primary={item.descripcion}
                            secondary={`${item.codigo}${item.codigoScanner ? ` - ${item.codigoScanner}` : ""}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                </Popper>
                
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleBuscar}
                  startIcon={<SearchIcon sx={{ color: "white" }} />}
                  sx={{
                    backgroundColor: "#5b5b5f",
                    textTransform: "none",
                    color: "white",
                    "&:hover": { backgroundColor: alpha("rgb(244, 177, 14)", 0.8) },
                    "& .MuiButton-startIcon svg": {
                      color: "white",
                    },
                  }}
                >
                  Aplicar
                </Button>
                {selectedItem && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setSelectedItem(null)
                      setSearchText("")
                      handleBuscar()
                    }}
                    sx={{ 
                      textTransform: "none",
                      color: "#5b5b5f",
                      borderColor: "#5b5b5f",
                      "&:hover": {
                        borderColor: "rgb(244, 177, 14)",
                        backgroundColor: alpha("rgb(244, 177, 14)", 0.1),
                      },
                    }}
                  >
                    Limpiar
                  </Button>
                )}
              </Box>
              
              {selectedItem && (
                <Box sx={{ mt: 2, p: 1, bgcolor: alpha(theme.palette.success.main, 0.1), borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.success.dark }}>
                    Producto seleccionado: {selectedItem.descripcion} ({selectedItem.codigo})
                  </Typography>
                </Box>
              )}
            </Paper>
          )}

          {/* Subbox de Canal */}
          {mostrarFiltroCanal && canalesEntidades && canalesEntidades.length > 0 && (
            <Paper
              elevation={2}
              sx={{
                p: 2,
                backgroundColor: alpha(theme.palette.background.default, 0.5),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="subtitle2" sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 600, color: "#5b5b5f" }}>
                  <ChannelIcon fontSize="small" sx={{ color: "rgb(244, 177, 14)" }} />
                  Canal de Entidad
                </Typography>
                <IconButton size="small" onClick={() => setMostrarFiltroCanal(false)} sx={{ color: "#5b5b5f" }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
                <Autocomplete
                  size="small"
                  disablePortal
                  options={[
                    { id: null, label: "Todos" },
                    ...canalesEntidades.map((canal) => ({
                      id: canal.id,
                      label: canal.descripcion,
                    })),
                  ]}
                  value={
                    selectedCanalEntidad
                      ? {
                          id: selectedCanalEntidad.id,
                          label: selectedCanalEntidad.label,
                        }
                      : { id: null, label: "Todos" }
                  }
                  onChange={(event, newValue) => setSelectedCanalEntidad(newValue?.id ? newValue : null)}
                  getOptionLabel={(option) => option.label || ""}
                  sx={{ flex: 1, minWidth: 200 }}
                  renderInput={(params) => <TextField {...params} label="Seleccionar canal" variant="outlined" />}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleBuscar}
                  startIcon={<SearchIcon sx={{ color: "white" }} />}
                  sx={{
                    backgroundColor: "#5b5b5f",
                    textTransform: "none",
                    color: "white",
                    "&:hover": { backgroundColor: alpha("rgb(244, 177, 14)", 0.8) },
                    "& .MuiButton-startIcon svg": {
                      color: "white",
                    },
                  }}
                >
                  Aplicar
                </Button>
                {selectedCanalEntidad && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setSelectedCanalEntidad(null)
                      handleBuscar()
                    }}
                    sx={{ 
                      textTransform: "none",
                      color: "#5b5b5f",
                      borderColor: "#5b5b5f",
                      "&:hover": {
                        borderColor: "rgb(244, 177, 14)",
                        backgroundColor: alpha("rgb(244, 177, 14)", 0.1),
                      },
                    }}
                  >
                    Limpiar
                  </Button>
                )}
              </Box>
            </Paper>
          )}

        </Box>
      )}
    </>
  )

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        width: "100%",
        overflow: "hidden",
      }}
    >
      <ThemeProvider theme={theme}>
        {/* Contenedor de filtros para Desktop (siempre visible) */}
        {!isLaptopDown && (
          <Collapse in={true} timeout={250}>
            <Paper
              className="diseñoFiltroTransaccionesVenta"
              elevation={3}
              sx={{
                p: 2,
                mb: 2,
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.background.paper, 0.9),
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                maxHeight: "40vh",
                overflow: "auto",
                flexShrink: 0,
                "&::-webkit-scrollbar": {
                  width: "8px",
                  height: "8px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.4),
                  borderRadius: "4px",
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.6),
                  },
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: alpha(theme.palette.grey[300], 0.3),
                  borderRadius: "4px",
                },
              }}
            >
              {renderFiltrosContent()}
            </Paper>
          </Collapse>
        )}

        {/* Menú lateral (solo desktop) */}
        {!isLaptopDown && (
          <Drawer
            anchor="right"
            open={sidebarOpen}
            onClose={toggleSidebar}
            variant="persistent"
            sx={{
              "& .MuiDrawer-paper": {
                width: 80,
                top: 0,
                height: "100vh",
                backgroundColor: alpha(theme.palette.background.paper, 0.98),
                borderLeft: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
                zIndex: 1200,
                overflowX: "hidden",
              },
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", height: "100%", p: 1 }}>
              {/* Botón de cerrar */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  mb: 2,
                  pb: 1,
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                }}
              >
                <IconButton size="small" onClick={toggleSidebar}>
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {/* 1. Atributos de Entidad */}
                <Tooltip title="Filtros por Atributos de Entidad" placement="left">
                  <IconButton
                    onClick={() => toggleFiltro('cliente')}
                    sx={{
                      color: mostrarFiltroCliente || Object.values(atributosEntidadFiltro).filter(v => v).length > 0 ? "rgb(244, 177, 14)" : "rgba(0, 0, 0, 0.54) !important",
                      "& svg": {
                        color: mostrarFiltroCliente || Object.values(atributosEntidadFiltro).filter(v => v).length > 0 ? "rgb(244, 177, 14)" : "rgba(0, 0, 0, 0.54) !important",
                      },
                      bgcolor: mostrarFiltroCliente ? alpha("rgb(244, 177, 14)", 0.1) : "transparent",
                      "&:hover": {
                        bgcolor: alpha("rgb(244, 177, 14)", 0.15),
                      },
                    }}
                  >
                    <Badge 
                      badgeContent={Object.values(atributosEntidadFiltro).filter(v => v).length > 0 ? 1 : 0} 
                      color="secondary"
                    >
                      <PersonIcon />
                    </Badge>
                  </IconButton>
                </Tooltip>

                {/* 2. Producto */}
                <Tooltip title="Filtros de Producto" placement="left">
                  <IconButton
                    onClick={() => toggleFiltro('producto')}
                    sx={{
                      color: mostrarFiltroProducto || selectedItem ? "rgb(244, 177, 14)" : "rgba(0, 0, 0, 0.54) !important",
                      "& svg": {
                        color: mostrarFiltroProducto || selectedItem ? "rgb(244, 177, 14)" : "rgba(0, 0, 0, 0.54) !important",
                      },
                      bgcolor: mostrarFiltroProducto ? alpha("rgb(244, 177, 14)", 0.1) : "transparent",
                      "&:hover": {
                        bgcolor: alpha("rgb(244, 177, 14)", 0.15),
                      },
                    }}
                  >
                    <Badge badgeContent={selectedItem ? 1 : 0} color="secondary">
                      <InventoryIcon />
                    </Badge>
                  </IconButton>
                </Tooltip>

                {/* 3. Canal */}
                {canalesEntidades && canalesEntidades.length > 0 && (
                  <Tooltip title="Filtros de Canal" placement="left">
                    <IconButton
                      onClick={() => toggleFiltro('canal')}
                      sx={{
                        color: mostrarFiltroCanal || selectedCanalEntidad ? "rgb(244, 177, 14)" : "rgba(0, 0, 0, 0.54) !important",
                        "& svg": {
                          color: mostrarFiltroCanal || selectedCanalEntidad ? "rgb(244, 177, 14)" : "rgba(0, 0, 0, 0.54) !important",
                        },
                        bgcolor: mostrarFiltroCanal ? alpha("rgb(244, 177, 14)", 0.1) : "transparent",
                        "&:hover": {
                          bgcolor: alpha("rgb(244, 177, 14)", 0.15),
                        },
                      }}
                    >
                      <Badge badgeContent={selectedCanalEntidad ? 1 : 0} color="secondary">
                        <ChannelIcon />
                      </Badge>
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
          </Drawer>
        )}

        {/* Contenedor de la tabla */}
        <Paper
          sx={{
            flex: 1,
            m: 2,
            mt: 0,
            borderRadius: 3,
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
            position: "relative",
            overflow: "hidden",
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <DataGrid
            key={isNavbarCollapsed ? "collapsed" : "expanded"}
            className="tablaVentasDataGrid"
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            rowCount={totalPages * 100}
            pagination
            paginationMode="server"
            getRowHeight={() => 35}
            headerHeight={35}
            // DataGrid usa 0-based index para páginas, por lo que se ajusta.
            onPageChange={(newPage) => setPage(newPage + 1)}
            sx={{
              width: "100%",
              border: 0,
              "& .MuiDataGrid-footerContainer": {
                display: "none !important",
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#5b5b5f",
                color: "white",
                borderBottom: "2px solid #333",
                height: "45px !important",
                minHeight: "45px !important",
                maxHeight: "45px !important",
              },
              "& .MuiDataGrid-columnHeader": {
                height: "45px !important",
                minHeight: "45px !important",
                maxHeight: "45px !important",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                color: "white !important",
                lineHeight: "45px !important",
              },
              "& .MuiDataGrid-columnHeaderTitle .MuiDataGrid-menuIconButton": {
                color: "white !important",
              },
              "& .MuiDataGrid-menuIconButton": {
                color: "rgb(244, 177, 14) !important",
              },
              "& .MuiInputBase-root": {
                color: "rgb(244, 177, 14) !important",
              },
              "& .MuiTablePagination-root": {
                color: "black !important",
              },
              "& .MuiSelect-icon": {
                color: "rgb(244, 177, 14) !important",
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid #ccc",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              },
              "& .MuiDataGrid-row": {
                "&:nth-of-type(even)": {
                  backgroundColor: "#F7F7F7",
                },
                "&:hover": {
                  backgroundColor: "#E3EAFD !important",
                  transition: "background 0.3s ease",
                },
              },
              "& .row-devolucion": {
                backgroundColor: alpha("#9e9e9e !important", 0.05),
                "&:hover": {
                  backgroundColor: alpha("#9e9e9e !important", 0.1),
                },
              },
              "& .MuiDataGrid-virtualScroller": {
                lineHeight: "none !important",
                height: "10px !imporant",
              },
              "& .MuiDataGrid-footerContainer": {
                position: "sticky",
                bottom: 0,
                backgroundColor: "#ffffff",
                borderTop: "1px solid #ddd",
                zIndex: 10,
              },
              "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
                width: "2px",
              },
              "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
                backgroundColor: "#888",
                borderRadius: "10px",
              },
            }}
          />
        </Paper>

        {/* Contenedor del footer (paginación) */}
        <Box
          sx={{
            flexShrink: 0,
            p: 1,
            backgroundColor: "#ffffff",
            borderRadius: 3,
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
            border: "1px solid rgba(0,0,0,0.03)",
          }}
        >
          <CustomPagination
            currentPage={page}
            totalPages={totalPages}
            totalCount={totalCount}
            montoTotalTransacciones={totalAmount}
            vieneDe={"tablaDetalle"}
            onPageChange={(newPage) => setPage(newPage)}
          />
        </Box>

        {/* Modal de gráficas */}
        <GraficasDetallesVentasModal
          open={openGraficasModal}
          handleClose={handleCloseGraficasModal}
          data={datosGraficas}
          loading={loadingGraficas}
        />
      </ThemeProvider>
    </div>
  );
};

export default TablaDetallesVentas;
